import React, { useState } from 'react';

import { AppAdminFormLabel, AppButton, AppMultiSelect, SearchTextField } from '@/components/atoms';
import { Download, Search } from '@/components/atoms/AppIcon';
import type { TenantDto } from '@/services/admin/tenants';
import { DEFAULT_SITES } from '@/constants/routeSegments';
import { useTheme } from '@shared/providers/theme/ThemeContext';

interface AdminUserListFilterProps {
  isSuperAdminRoute: boolean;
  rootTenants: TenantDto[];
  defaultSearchName: string;
  defaultSearchEmail: string;
  defaultSearchManageSites: string[];
  onSearch: (filters: { name: string; email: string; manageSites: string[] }) => void;
  onCsv: () => void;
  csvLoading?: boolean;
}

const AdminUserListFilter: React.FC<AdminUserListFilterProps> = ({
  isSuperAdminRoute,
  rootTenants,
  defaultSearchName,
  defaultSearchEmail,
  defaultSearchManageSites,
  onSearch,
  onCsv,
  csvLoading = false,
}) => {
  const { colors } = useTheme();

  const [searchName, setSearchName] = useState(defaultSearchName);
  const [searchEmail, setSearchEmail] = useState(defaultSearchEmail);
  const [searchManageSites, setSearchManageSites] = useState<string[]>(defaultSearchManageSites);

  const csvButtonStyle = {
    backgroundColor: colors.secondary,
    ['--action-bg-hover' as string]: colors.secondaryHover,
  } as React.CSSProperties;

  const handleSearch = () => {
    onSearch({ name: searchName, email: searchEmail, manageSites: searchManageSites });
  };

  return (
    <div className="bg-white rounded-md border p-4 mb-4">
      <div className={`grid gap-4 items-end ${isSuperAdminRoute ? 'grid-cols-4' : 'grid-cols-3'}`}>
        {isSuperAdminRoute && (
          <div>
            <AppAdminFormLabel label="サイト区分" />
            <AppMultiSelect
              placeholder="サイト区分"
              value={searchManageSites}
              className="w-full"
              maxTagCount="responsive"
              onInputKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              options={[
                { label: 'すべて', value: 'all' },
                { label: 'スーパー管理者', value: DEFAULT_SITES.SUPER_ADMIN },
                ...rootTenants.map(t => ({ label: t.name, value: t.site_type })),
              ]}
              onChange={values => {
                const next = (values as string[]) || [];
                if (next.includes('all')) {
                  setSearchManageSites([
                    DEFAULT_SITES.SUPER_ADMIN,
                    ...rootTenants.map(t => t.site_type),
                  ]);
                  return;
                }
                setSearchManageSites(next.filter(v => v !== 'all'));
              }}
            />
          </div>
        )}
        <div>
          <AppAdminFormLabel label="管理者名" />
          <SearchTextField
            value={searchName}
            onChange={e => setSearchName(e.target.value)}
            onPressEnter={handleSearch}
            data-skip-enter-nav
            placeholder="管理者名"
            className="w-full"
          />
        </div>
        <div>
          <AppAdminFormLabel label="メールアドレス" />
          <SearchTextField
            value={searchEmail}
            onChange={e => setSearchEmail(e.target.value)}
            onPressEnter={handleSearch}
            data-skip-enter-nav
            placeholder="メールアドレス"
            className="w-full"
          />
        </div>
        <div className="flex items-end gap-2">
          <AppButton type="primary" icon={<Search />} onClick={handleSearch}>
            検索
          </AppButton>
          <AppButton
            type="primary"
            icon={<Download />}
            disabled={csvLoading}
            loading={csvLoading}
            onClick={onCsv}
            style={csvButtonStyle}
            className="hover:!bg-[var(--action-bg-hover)]"
          >
            CSV
          </AppButton>
        </div>
      </div>
    </div>
  );
};

export default AdminUserListFilter;
